import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import {
  HS_CODE_MAPPINGS,
  HSCodeMapping,
  searchHSCodes,
} from "../../../utils/hsCodeMapping";
import DeletableChip from "./DeletableChip";

interface VirtualizedHSCodeDropdownProps {
  selectedCodes: string[];
  onCodesChange: (codes: string[]) => void;
  placeholder?: string;
  label: string;
}

// Custom option component for better performance
const HSCodeOption = React.memo(
  ({ option, props }: { option: HSCodeMapping; props: any }) => {
    const { key, ...otherProps } = props;
    return (
      <Box
        component="li"
        {...otherProps}
        sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
      >
        <Box>
          <Typography
            variant="body2"
            component="span"
            sx={{ fontWeight: 500, color: "#1976d2" }}
          >
            {option.code}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            sx={{ ml: 1, color: "#ffffff" }}
          >
            {option.displayName.split(":")[1]?.trim() || option.displayName}
          </Typography>
        </Box>
      </Box>
    );
  }
);

const VirtualizedHSCodeDropdown: React.FC<VirtualizedHSCodeDropdownProps> = ({
  selectedCodes,
  onCodesChange,
  placeholder = "Search HS codes...",
  label,
}) => {
  const [inputValue, setInputValue] = useState("");

  // Optimized filtering with smart ranking for better UX
  const filteredOptions = useMemo(() => {
    return searchHSCodes(inputValue, 200);
  }, [inputValue]);

  const selectedOptions = useMemo(() => {
    return HS_CODE_MAPPINGS.filter((option) =>
      selectedCodes.includes(option.code)
    );
  }, [selectedCodes]);

  const handleChange = useCallback(
    (event: any, newValue: HSCodeMapping[]) => {
      const newCodes = newValue.map((option) => option.code);
      onCodesChange(newCodes);
    },
    [onCodesChange]
  );

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Autocomplete
      multiple
      id={`hs-codes-autocomplete-${label.toLowerCase().replace(/\s+/g, "-")}`}
      options={filteredOptions}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option: HSCodeMapping) =>
        `${option.code}: ${option.displayName.split(":")[1]?.trim() || option.displayName}`
      }
      isOptionEqualToValue={(option: HSCodeMapping, value: HSCodeMapping) =>
        option.code === value.code
      }
      filterOptions={(options) => options} // We handle filtering manually for performance
      limitTags={3}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedOptions.length === 0 ? placeholder : ""}
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              color: "#e0e0e0",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            },
            shrink: selectedOptions.length > 0 || inputValue.length > 0,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1e1e1e",
              "& fieldset": {
                borderColor: "#555",
              },
              "&:hover fieldset": {
                borderColor: "#777",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
            "& .MuiInputBase-input": {
              color: "#e0e0e0",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#888888",
              opacity: 1,
            },
            "& .MuiFormHelperText-root": {
              color: "#888888",
              fontSize: "0.75rem",
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            paddingTop: "2px",
          }}
        >
          {tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            const tooltipText =
              option.displayName.split(":")[1]?.trim() || option.displayName;
            return (
              <DeletableChip
                key={option.code}
                label={option.code}
                tooltip={tooltipText}
                onDelete={() => {
                  if (tagProps.onDelete) {
                    tagProps.onDelete({} as any);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      renderOption={(props, option: HSCodeMapping) => (
        <HSCodeOption key={option.code} option={option} props={props} />
      )}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxHeight: "300px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            '&[aria-selected="true"]': {
              backgroundColor: "#1976d2 !important",
            },
            "&.Mui-focused": {
              backgroundColor: "#333333 !important",
            },
          },
        },
        "& .MuiAutocomplete-popper": {
          "& .MuiPaper-root": {
            backgroundColor: "#262626",
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
    />
  );
};

export default VirtualizedHSCodeDropdown;
